/**
 * Dynamic EOD Form - Renders form questions from the database config
 * Used by SdrEodForm, CloserEodForm, and DmEodForm
 */

import { useState, useEffect } from "react";
import {
  EODFormLayout,
  FormField,
  TextInput,
  TextArea,
  Select,
  RatingInput,
  SubmitButton,
  SuccessMessage,
  ErrorMessage,
} from "./EODFormLayout";

interface FormOption {
  value: string;
  label: string;
}

interface FormQuestion {
  id: string;
  label: string;
  type: "select" | "text" | "number" | "date" | "textarea" | "rating";
  required: boolean;
  hint?: string;
  placeholder?: string;
  options?: FormOption[];
  section: string;
  sectionTitle?: string;
  min?: number;
  max?: number;
  dbField: string;
}

interface FormConfig {
  formType: string;
  title: string;
  subtitle: string;
  questions: FormQuestion[];
  nameOptions: FormOption[];
}

function getTodayDate(): string {
  return new Date().toISOString().split("T")[0] ?? "";
}

export function DynamicEodForm({
  formType,
  submitUrl,
  fallbackTitle,
  fallbackSubtitle,
}: {
  formType: "sdr" | "closer" | "dm";
  submitUrl: string;
  fallbackTitle: string;
  fallbackSubtitle: string;
}) {
  const [config, setConfig] = useState<FormConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [formData, setFormData] = useState<Record<string, string | number | undefined>>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch form config from API
  useEffect(() => {
    fetch(`/api/eod/forms/${formType}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data) {
          setConfig(data.data);
          // Initialize form data with empty values
          const initial: Record<string, string | number | undefined> = {
            name: "",
            eodDate: getTodayDate(),
          };
          for (const q of data.data.questions) {
            initial[q.id] = q.type === "rating" ? undefined : "";
          }
          setFormData(initial);
        }
      })
      .catch(console.error)
      .finally(() => setConfigLoading(false));
  }, [formType]);

  const updateField = (field: string, value: string | number | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Build the submission payload using dbField mapping
      const payload: Record<string, unknown> = {
        name: formData.name,
        eodDate: formData.eodDate,
      };

      if (config) {
        for (const q of config.questions) {
          const rawValue = formData[q.id];
          if (q.type === "number") {
            payload[q.dbField] = parseInt(String(rawValue)) || 0;
          } else if (q.type === "rating") {
            payload[q.dbField] = rawValue;
          } else {
            payload[q.dbField] = rawValue || "";
          }
        }
      }

      const response = await fetch(submitUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to submit EOD");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (config) {
      const initial: Record<string, string | number | undefined> = {
        name: "",
        eodDate: getTodayDate(),
      };
      for (const q of config.questions) {
        initial[q.id] = q.type === "rating" ? undefined : "";
      }
      setFormData(initial);
    }
    setSubmitted(false);
    setError(null);
  };

  // Loading state
  if (configLoading) {
    return (
      <EODFormLayout title={fallbackTitle} subtitle={fallbackSubtitle}>
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-red-500/20 border-t-red-500 rounded-full animate-spin" />
        </div>
      </EODFormLayout>
    );
  }

  // If config failed to load, show error
  if (!config) {
    return (
      <EODFormLayout title={fallbackTitle} subtitle={fallbackSubtitle}>
        <div className="text-center py-12">
          <p className="text-white/60">Failed to load form configuration.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 text-red-400 hover:text-red-300 underline"
          >
            Retry
          </button>
        </div>
      </EODFormLayout>
    );
  }

  if (submitted) {
    return (
      <EODFormLayout title={config.title} subtitle={config.subtitle}>
        <SuccessMessage onReset={handleReset} />
      </EODFormLayout>
    );
  }

  // Group questions by section
  const sections: { key: string; title: string; questions: FormQuestion[] }[] = [];
  const sectionMap = new Map<string, { title: string; questions: FormQuestion[] }>();

  for (const q of config.questions) {
    let section = sectionMap.get(q.section);
    if (!section) {
      section = { title: q.sectionTitle || "", questions: [] };
      sectionMap.set(q.section, section);
      sections.push({ key: q.section, ...section });
    }
    section.questions.push(q);
  }

  return (
    <EODFormLayout title={config.title} subtitle={config.subtitle}>
      {error && <ErrorMessage message={error} onRetry={() => setError(null)} />}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name & Date - always present */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Who is filling this form out?" required>
            <Select
              value={String(formData.name || "")}
              onChange={(v) => updateField("name", v)}
              options={config.nameOptions}
              placeholder="Select your name"
              required
            />
          </FormField>

          <FormField label="What date is this EOD for?" required>
            <TextInput
              type="date"
              value={String(formData.eodDate || "")}
              onChange={(v) => updateField("eodDate", v)}
              required
            />
          </FormField>
        </div>

        {/* Dynamic Sections */}
        {sections.map((section) => (
          <div key={section.key}>
            {section.title && (
              <div className="border-t border-white/10 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">{section.title}</h3>
              </div>
            )}
            {!section.title && section.key !== "basic" && (
              <div className="border-t border-white/10 pt-6" />
            )}

            {/* Determine layout based on question types */}
            {renderSectionQuestions(section.questions, formData, updateField)}
          </div>
        ))}

        {/* Submit */}
        <div className="pt-4">
          <SubmitButton loading={loading} />
        </div>
      </form>
    </EODFormLayout>
  );
}

function renderSectionQuestions(
  questions: FormQuestion[],
  formData: Record<string, string | number | undefined>,
  updateField: (field: string, value: string | number | undefined) => void
) {
  // Group number fields together in a grid, others in a stack
  const numberFields = questions.filter((q) => q.type === "number");
  const otherFields = questions.filter((q) => q.type !== "number");

  // If all fields are numbers, render in grid
  if (numberFields.length > 0 && otherFields.length === 0) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {numberFields.map((q) => renderQuestion(q, formData, updateField))}
      </div>
    );
  }

  // If we have a mix, render numbers in grid first, then others stacked
  if (numberFields.length > 1) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {numberFields.map((q) => renderQuestion(q, formData, updateField))}
        </div>
        {otherFields.map((q) => renderQuestion(q, formData, updateField))}
      </div>
    );
  }

  // Default: stack all
  return (
    <div className="space-y-4">
      {questions.map((q) => renderQuestion(q, formData, updateField))}
    </div>
  );
}

function renderQuestion(
  q: FormQuestion,
  formData: Record<string, string | number | undefined>,
  updateField: (field: string, value: string | number | undefined) => void
) {
  const value = formData[q.id];

  switch (q.type) {
    case "select":
      return (
        <FormField key={q.id} label={q.label} required={q.required} hint={q.hint}>
          <Select
            value={String(value || "")}
            onChange={(v) => updateField(q.id, v)}
            options={q.options || []}
            placeholder={q.placeholder || "Select an option"}
            required={q.required}
          />
        </FormField>
      );

    case "number":
      return (
        <FormField key={q.id} label={q.label} required={q.required} hint={q.hint}>
          <TextInput
            type="number"
            value={String(value || "")}
            onChange={(v) => updateField(q.id, v)}
            placeholder={q.placeholder || "0"}
            min={q.min ?? 0}
            required={q.required}
          />
        </FormField>
      );

    case "textarea":
      return (
        <FormField key={q.id} label={q.label} required={q.required} hint={q.hint}>
          <TextArea
            value={String(value || "")}
            onChange={(v) => updateField(q.id, v)}
            placeholder={q.placeholder}
            rows={3}
            required={q.required}
          />
        </FormField>
      );

    case "rating":
      return (
        <FormField key={q.id} label={q.label} required={q.required} hint={q.hint}>
          <RatingInput
            value={typeof value === "number" ? value : undefined}
            onChange={(v) => updateField(q.id, v)}
            max={q.max ?? 10}
          />
        </FormField>
      );

    case "date":
      return (
        <FormField key={q.id} label={q.label} required={q.required} hint={q.hint}>
          <TextInput
            type="date"
            value={String(value || "")}
            onChange={(v) => updateField(q.id, v)}
            required={q.required}
          />
        </FormField>
      );

    case "text":
    default:
      return (
        <FormField key={q.id} label={q.label} required={q.required} hint={q.hint}>
          <TextInput
            type="text"
            value={String(value || "")}
            onChange={(v) => updateField(q.id, v)}
            placeholder={q.placeholder}
            required={q.required}
          />
        </FormField>
      );
  }
}
